import { cancelEvent, cn, extractDeploymentUrl } from "@/lib/utils";
import { useMessageStore } from "@/state/stores";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Clock,
  Cloud,
  Copy,
  ExternalLink,
  Loader,
  Sparkles,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useShallow } from "zustand/shallow";
import type { FpFile, FpMessagePart } from "../../../../../schemas/codegen";
import { ExpandableMessageCard } from "./shared";

interface DeployProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
  messageId: string;
}

interface DeploymentStep {
  name: string;
  status: "pending" | "processing" | "completed";
  content?: string;
  hasError: boolean;
}

interface DeploymentStatus {
  hasError: boolean;
  isCompleted: boolean;
  isProcessing: boolean;
  deploymentUrl: string | null;
}

const EMPTY: FpFile[] = [];

// Separate component for step icon
const StepIcon = ({ step }: { step: DeploymentStep }) => {
  if (step.hasError) {
    return <AlertCircle className="w-4 h-4" />;
  }

  if (
    step.status === "completed" &&
    // Hack: avoid using the status completed for `deploy` steps
    // as it is a temporary step that gets replaced once the deploy kicks
    // off.
    // See: https://linear.app/fiberplane/issue/FP-4905/initial-deploy-state-seems-to-be-donesuccess
    step.name !== "deploy"
  ) {
    return <CheckCircle2 className="w-4 h-4 text-success" />;
  }

  if (step.status === "processing") {
    if (step.name === "Assigning Custom Domains") {
      return <Sparkles className="w-4 h-4 animate-pulse" />;
    }

    return <Loader className="w-4 h-4 animate-spin" />;
  }

  return <Clock className="w-4 h-4" />;
};

// Separate component for deployment URL with copy functionality
const DeploymentUrl = ({
  url,
  className,
}: { url: string; className?: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    cancelEvent(e);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div
      className={cn("flex flex-1 items-center gap-2 justify-end", className)}
    >
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-muted-foreground transition-colors justify-end"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="font-mono break-all underline">{url}</span>
      </a>

      <button
        type="button"
        onClick={handleCopy}
        className={cn(
          "p-0.5 rounded-full transition-colors",
          !copied && "hover:text-muted-foreground",
        )}
        title="Copy URL"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};

export function Deploy({ part, messageId }: DeployProps) {
  // Use shallow selector to get stable reference to deployment files
  const deploymentFiles: FpFile[] = useMessageStore(
    useShallow((state) => state.deploymentStatus[messageId] || EMPTY),
  );

  // Memoize deployment steps processing
  const deploymentSteps = useMemo((): DeploymentStep[] => {
    return deploymentFiles.map((file) => ({
      name: file.filename.replace("Deploy Status: ", ""),
      status: file.status,
      content: file.content,
      hasError: file.content?.includes("status: error") ?? false,
    }));
  }, [deploymentFiles]);

  // Memoize the computed status values
  const deploymentStatus = useMemo((): DeploymentStatus => {
    const hasError = deploymentSteps.some((step) => step.hasError);
    const isCompleted =
      part.status === "complete" &&
      deploymentSteps.some(
        (step) =>
          step.status === "completed" && step.content?.includes("successfully"),
      );
    const isProcessing =
      part.status === "pending" ||
      deploymentSteps.some((step) => step.status === "processing");

    // Extract deployment URL from completed step
    const completedStep = deploymentSteps.find(
      (step) => step.status === "completed" && step.content?.includes("fp.dev"),
    );
    const deploymentUrl = extractDeploymentUrl(completedStep?.content);

    return {
      hasError,
      isCompleted,
      isProcessing,
      deploymentUrl,
    };
  }, [deploymentSteps, part.status]);

  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={Cloud}
      title={"Deploy"}
      expanded
      additionalContent={
        deploymentStatus.deploymentUrl && (
          <div className="flex items-center gap-2 flex-1 justify-end">
            <Check className="w-4 h-4 flex-shrink-0 text-success" />
            <a
              href={deploymentStatus.deploymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-mono text-muted-foreground hover:text-foreground transition-colors underline truncate"
              onClick={(e) => e.stopPropagation()}
            >
              {deploymentStatus.deploymentUrl}
            </a>
          </div>
        )
      }
      accordionValue="deploy"
    >
      <div className="space-y-4">
        {deploymentSteps.map((step, index) => (
          <div
            key={`${step.name}-${index}`}
            className={cn("flex items-center justify-between", {
              "text-destructive": step.hasError,
            })}
          >
            <div className="flex items-center gap-3">
              <StepIcon step={step} />
              <span>{step.name}</span>
            </div>
            {step.hasError && <span className="text-sm font-mono">Error</span>}
          </div>
        ))}

        {deploymentStatus.deploymentUrl && (
          <div className="flex items-center justify-between h-5">
            <div className="flex items-center gap-3">
              <ExternalLink className="w-4 h-4 flex-shrink-0" />
              <span className="mr-[1ch]">API available at</span>
            </div>
            <DeploymentUrl url={deploymentStatus.deploymentUrl} />
          </div>
        )}
      </div>
    </ExpandableMessageCard>
  );
}
